import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { IconButton } from '@mes-amis/galette-design-system';
import { useBanners } from '../hooks/useBanner';
import { useLockContext } from '../contexts/LockContext';
import { dismissBanner, getBanner, highestSeverity, type BannerMessage, type BannerSeverity } from '@/lib/global-banner';

/** Rows shown before the rest fold behind "Show N more". */
export const MAX_VISIBLE_ROWS = 3;

const CONTAINER: Record<BannerSeverity, string> = {
  critical: 'bg-[#f8eeec] border-[#f1dcd9]',
  warning: 'bg-yellow-50 border-yellow-200',
  info: 'bg-white border-gray-200',
};
const TEXT: Record<BannerSeverity, string> = {
  critical: 'font-semibold text-gray-900',
  warning: 'text-yellow-800',
  info: 'text-gray-800',
};
const ICON: Record<BannerSeverity, string> = {
  critical: 'text-red-500',
  warning: 'text-yellow-600',
  info: 'text-gray-500',
};
const ACTION = 'font-semibold text-[#5e6fcf] hover:underline disabled:opacity-50 disabled:no-underline';

function BannerActionLink({ message }: { message: BannerMessage }) {
  const { action } = message;
  if (!action) return null;
  if (action.to) return <Link to={action.to} className={ACTION}>{action.label}</Link>;
  if (action.href) {
    return <a href={action.href} target="_blank" rel="noopener noreferrer" className={ACTION}>{action.label}</a>;
  }
  return (
    <button
      type="button"
      className={ACTION}
      disabled={action.disabled}
      // The stored message may hold a newer closure than this render's copy.
      onClick={() => (getBanner(message.id)?.action ?? action).onClick?.()}
    >
      {action.label}
    </button>
  );
}

function BannerRow({ message }: { message: BannerMessage }) {
  const Icon = message.severity === 'info' ? InformationCircleIcon : ExclamationTriangleIcon;
  return (
    <div
      data-testid="global-banner-message"
      data-banner-id={message.id}
      data-severity={message.severity}
      className="flex items-center gap-2"
    >
      <Icon className={`w-5 h-5 shrink-0 ${ICON[message.severity]}`} aria-hidden="true" />
      <p className={`flex-1 text-sm ${TEXT[message.severity]}`}>
        {message.text}
        {message.action && <> <BannerActionLink message={message} /></>}
      </p>
      {message.dismissible && (
        <IconButton icon="close" size="sm" alt="Dismiss" onClick={() => dismissBanner(message.id)} />
      )}
    </div>
  );
}

/**
 * The single place app- and device-wide messages appear (see
 * src/lib/global-banner.ts). Every visible message is listed in one
 * container, most important first, colored by the most severe; past
 * MAX_VISIBLE_ROWS the rest fold behind "Show N more". Hidden on the lock
 * screen.
 */
export default function GlobalBanner() {
  const messages = useBanners();
  const { isLocked } = useLockContext();
  const [expanded, setExpanded] = useState(false);

  if (isLocked || messages.length === 0) return null;

  const severity = highestSeverity(messages);
  const rows = expanded ? messages : messages.slice(0, MAX_VISIBLE_ROWS);
  const folded = messages.length - rows.length;

  return (
    <div
      data-testid="global-banner"
      data-severity={severity}
      role={severity === 'critical' ? 'alert' : 'status'}
      className={`${CONTAINER[severity]} border rounded-lg flex flex-col gap-1.5 px-3 py-2 mx-6 mt-4 shrink-0`}
    >
      {rows.map((message) => <BannerRow key={message.id} message={message} />)}
      {messages.length > MAX_VISIBLE_ROWS && (
        <button
          type="button"
          className="self-start pl-7 text-xs font-semibold text-gray-600 hover:underline"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : `Show ${folded} more`}
        </button>
      )}
    </div>
  );
}
